import type { CatalogItem } from "../types"

export function formatPrice(amount: number | null, currency = "EUR"): string {
  if (amount == null) return "Preis auf Anfrage"
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

/** "P3M" -> 3, "P12M" -> 12. */
export function durationMonths(iso?: string): number | null {
  if (!iso) return null
  const m = /^P(\d+)M$/i.exec(iso.trim())
  return m ? Number(m[1]) : null
}

/** "P3M" -> "3 Monate". */
export function formatDuration(iso?: string): string | null {
  const months = durationMonths(iso)
  if (months == null) return iso ?? null
  return `${months} ${months === 1 ? "Monat" : "Monate"}`
}

/** Einheitliche Bezeichnung der Milieu-Minds-Leistung (Vorgabe SINUS, 22.09.2026). */
export const MINDS_FEATURE = "2 Milieu Minds pro Milieu"

export function kindLabel(item: CatalogItem): string {
  switch (item.kind) {
    case "navigator":
      return "Navigator-Lizenz"
    case "bundle":
      return item.bundle_type === "staffel" ? "Staffelpreis" : "Bundle"
    case "infopackage":
      return "Infopaket"
    case "event":
      return "Vortrag"
    default:
      return "Produkt"
  }
}

/** Wie viele Infopakete muss der Kunde bei diesem Bundle/Staffel waehlen?
 *  Premium-Bundle: 2 (lt. Bestellformular 2026). Staffel: aus `count` (2/3/4). */
export function requiredPackageCount(item: CatalogItem): number {
  if (item.kind !== "bundle") return 0
  if (item.bundle_type === "premium") return 2
  if (item.bundle_type === "staffel") {
    const n = Number(item.count)
    return Number.isFinite(n) && n > 0 ? n : 0
  }
  return 0
}

/** Enthaelt dieses Bundle/Staffel den Premium-Status?
 *  Premium-Bundle: ja. Staffel ab 3 Paketen: ja (Staffel-2: nein). */
export function includesPremium(item: CatalogItem): boolean {
  if (item.kind !== "bundle") return false
  if (item.bundle_type === "premium") return true
  if (item.bundle_type === "staffel") {
    const n = Number(item.count)
    return Number.isFinite(n) && n >= 3
  }
  return false
}

/** Leistungsumfang fuer die Feature-Liste der Produktkarte. */
/** Fair-Use-Grenze je Navigator-Lizenz. Gilt pro Kalendermonat (token_meter). */
export const TOKEN_FEATURE = "2 Mio. Token pro Monat (Fair-Use)"

export function featureList(item: CatalogItem): string[] {
  if (item.kind === "navigator") {
    const d = formatDuration(item.duration)
    return [d ? `${d} Laufzeit` : "Feste Laufzeit", MINDS_FEATURE, TOKEN_FEATURE]
  }
  if (item.kind === "bundle" && item.bundle_type === "premium") {
    return [
      "12 Monate Laufzeit",
      MINDS_FEATURE,
      TOKEN_FEATURE,
      "2 frei wählbare Infopakete",
      "Online-Beratungsgespräch",
      "Premium-Status",
    ]
  }
  if (item.kind === "bundle" && item.bundle_type === "staffel") {
    const n = requiredPackageCount(item)
    const out = [`${n} frei wählbare Infopakete`, "Dauerhafter Zugang"]
    if (includesPremium(item)) out.push("Premium-Status")
    return out
  }
  if (item.kind === "infopackage") return ["Dauerhafter Zugang über das SINUS Infoportal"]
  if (item.kind === "event") return eventFeatures(item)
  return []
}

/** Vortraege: Dauer und Format stehen im Stripe-Namen ("... (60 min., online)").
 *  Ohne Angaben (z. B. "Preis auf Anfrage") die offenen Varianten zeigen. */
function eventFeatures(item: CatalogItem): string[] {
  const name = item.name
  const out: string[] = []
  const minutes = /(\d+)\s*min/i.exec(name)
  if (minutes) out.push(`${minutes[1]} Minuten`)
  else if (item.amount_net == null) out.push("Individuelle Länge")
  if (/online/i.test(name)) out.push("Online")
  else if (item.amount_net == null) out.push("Online oder vor Ort")
  return out
}

/** Titel ohne den Klammerzusatz, dessen Angaben bereits als Features erscheinen. */
export function cardTitle(item: CatalogItem): string {
  if (item.kind !== "event") return item.name
  return item.name.replace(/\s*\([^)]*\)\s*$/, "").trim()
}

/** Features, zu denen ein Info-Tooltip gehoert (Deeplink-Dialog). */
export const FEATURE_INFO: Record<string, string> = {
  [TOKEN_FEATURE]:
    "Umfangreiche normale Nutzung. Kein automatischer Nachkauf, keine Zusatzkosten.",
  "Premium-Status":
    "Vergünstigter Navigator-Zugang, Online-Beratungsgespräch, exklusive Booklets und Webinar-Aufzeichnungen im SINUS Infoportal.",
}

/**
 * Ersparnis gegenueber dem Einzelbezug, in Euro netto.
 *
 * Reihenfolge:
 *  1. Stripe-Metadatum `save_net` (falls gepflegt) - damit laesst sich der Wert
 *     spaeter ohne Code-Aenderung steuern.
 *  2. Berechnung aus dem Katalog:
 *       - Navigator 12 Monate: 4x die 3-Monats-Lizenz
 *       - Staffel-N:           N x Einzelpreis eines Infopakets
 *  3. Premium-Bundle: fester Wert, siehe PREMIUM_BUNDLE_SAVING.
 */
export const PREMIUM_BUNDLE_SAVING = 1950 // Vorgabe SINUS, 22.09.2026

export function savingNet(item: CatalogItem, catalog: CatalogItem[]): number | null {
  const meta = Number(item.save_net)
  if (Number.isFinite(meta) && meta > 0) return meta

  const price = item.amount_net
  if (price == null) return null

  if (item.kind === "navigator" && durationMonths(item.duration) === 12) {
    const quarter = catalog.find(
      (i) => i.kind === "navigator" && durationMonths(i.duration) === 3,
    )?.amount_net
    if (quarter != null) {
      const diff = quarter * 4 - price
      return diff > 0 ? diff : null
    }
    return null
  }

  if (item.kind === "bundle" && item.bundle_type === "premium") {
    return PREMIUM_BUNDLE_SAVING
  }

  if (item.kind === "bundle" && item.bundle_type === "staffel") {
    const single = catalog.find((i) => i.kind === "infopackage")?.amount_net
    const n = requiredPackageCount(item)
    if (single != null && n > 0) {
      const diff = single * n - price
      return diff > 0 ? diff : null
    }
  }

  return null
}
